import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  FUNDRAISING_PRICING_REGISTRY,
  FUNDRAISING_COMPARISON_DEVELOPMENT_MODE,
  getAvailablePricingConfigurations,
  getPricingConfiguration,
} from '../../data/platform-pricing.config';
import { evidenceBadgeClasses } from '../../data/evidence-presentation';
import type {
  FundraiserScenario,
  FundraisingComparisonState,
  PlatformComparisonResult,
  PlatformId,
  PlatformOptionChange,
  PlatformOptionGroup,
  PricingSourceOpen,
} from '../../models/fundraising-comparison.models';
import { ComparisonAnalyticsService } from '../../services/comparison-analytics.service';
import { FundraisingCalculationCoordinator } from '../../services/fundraising-calculation-coordinator';
import { UrlScenarioStateService } from '../../services/url-scenario-state.service';
import { CalculationDetailsComponent } from '../calculation-details/calculation-details.component';
import { FundraiserScenarioComponent } from '../fundraiser-scenario/fundraiser-scenario.component';
import { PlatformOptionsComponent } from '../platform-options/platform-options.component';

@Component({
  standalone: true,
  selector: 'fundraising-versus',
  imports: [
    CommonModule,
    FundraiserScenarioComponent,
    PlatformOptionsComponent,
    CalculationDetailsComponent,
  ],
  templateUrl: './fundraising-versus.component.html',
  styleUrls: [
    '../fundraising-comparison/fundraising-comparison.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundraisingVersusComponent implements OnInit, OnDestroy {
  @Input() leftPlatformId = '';
  @Input() rightPlatformId = '';

  readonly developmentMode = FUNDRAISING_COMPARISON_DEVELOPMENT_MODE;
  evidenceBadgeClasses = evidenceBadgeClasses;

  state!: FundraisingComparisonState;
  scenario?: FundraiserScenario;
  results: PlatformComparisonResult[] = [];
  errorMessage = '';
  liveAnnouncement = '';
  copyStatus = '';

  private readonly calculationCoordinator =
    new FundraisingCalculationCoordinator();
  private urlUpdateTimeout?: ReturnType<typeof setTimeout>;
  private readonly popStateListener = () => this.restoreFromUrl();

  constructor(
    private readonly urlState: UrlScenarioStateService,
    private readonly analytics: ComparisonAnalyticsService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.state = this.urlState.read();
    const error = this.validatePlatformIds();
    if (error) {
      this.errorMessage = error;
      this.cdr.markForCheck();
      return;
    }

    this.recalculate(false);
    window.addEventListener('popstate', this.popStateListener);
    this.analytics.track('fundraising_versus_loaded', {
      left_platform: this.leftPlatformId,
      right_platform: this.rightPlatformId,
      page: 'versus',
      pricing_mode: this.developmentMode ? 'development' : 'verified',
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('popstate', this.popStateListener);
    if (this.urlUpdateTimeout) clearTimeout(this.urlUpdateTimeout);
  }

  onIntendedGiftTotalCentsChange(intendedGiftTotalCents: number): void {
    this.state = { ...this.state, intendedGiftTotalCents };
    this.recalculate();
    this.trackScenarioChange();
  }

  onDonationCountOverrideChange(donationCountOverride: number | null): void {
    this.state = { ...this.state, donationCountOverride };
    this.recalculate();
    this.trackScenarioChange();
  }

  onOptionChange(change: PlatformOptionChange): void {
    if (
      change.platformId !== this.leftPlatformId &&
      change.platformId !== this.rightPlatformId
    ) {
      return;
    }

    this.state = {
      ...this.state,
      optionSelections: {
        ...this.state.optionSelections,
        [change.platformId]: {
          ...this.state.optionSelections[change.platformId],
          [change.optionGroupId]: change.choiceId,
        },
      },
    };
    this.recalculate();
    this.analytics.track(
      change.platformId === this.leftPlatformId
        ? 'fundraising_versus_left_option_changed'
        : 'fundraising_versus_right_option_changed',
      {
        platform: change.platformId,
        option_group: change.optionGroupId,
        choice: change.choiceId,
        page: 'versus',
      },
    );
  }

  onDetailsToggle(event: Event): void {
    const details = event.target as HTMLDetailsElement;
    if (!details.open) return;
    this.analytics.track('fundraising_versus_details_opened', {
      left_platform: this.leftPlatformId,
      right_platform: this.rightPlatformId,
      page: 'versus',
    });
  }

  onPlatformLinkClicked(platformId: PlatformId): void {
    this.analytics.track('fundraising_versus_platform_link_clicked', {
      platform: platformId,
      left_platform: this.leftPlatformId,
      right_platform: this.rightPlatformId,
      page: 'versus',
    });
  }

  onOverviewLinkClicked(): void {
    this.analytics.track('fundraising_versus_overview_link_clicked', {
      left_platform: this.leftPlatformId,
      right_platform: this.rightPlatformId,
      page: 'versus',
    });
  }

  onPricingSourceOpened(event: PricingSourceOpen): void {
    this.analytics.track('fundraising_pricing_source_opened', {
      platform: event.platformId,
      source_id: event.sourceId,
      page: 'versus',
    });
  }

  async copyVersusLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(
        this.urlState.buildAbsoluteUrl(this.state),
      );
      this.copyStatus = 'Comparison link copied.';
      this.analytics.track('fundraising_versus_link_copied', {
        left_platform: this.leftPlatformId,
        right_platform: this.rightPlatformId,
        page: 'versus',
      });
    } catch {
      this.copyStatus =
        'Unable to copy the link. Copy it from your browser address bar.';
    }
    this.cdr.markForCheck();
  }

  get leftResult(): PlatformComparisonResult | undefined {
    return this.results.find(
      (result) => result.platformId === this.leftPlatformId,
    );
  }

  get rightResult(): PlatformComparisonResult | undefined {
    return this.results.find(
      (result) => result.platformId === this.rightPlatformId,
    );
  }

  get receiptDifferenceCents(): number {
    if (!this.leftResult || !this.rightResult) return 0;
    return Math.abs(
      this.leftResult.organizationRetainedCents -
        this.rightResult.organizationRetainedCents,
    );
  }

  get receiptDifferenceLabel(): string {
    if (!this.leftResult || !this.rightResult) return '';
    if (this.receiptDifferenceCents === 0) {
      return 'The organization receives the same amount in both scenarios.';
    }
    return `${this.formatMoney(
      this.receiptDifferenceCents,
    )} difference in organization receipts.`;
  }

  get supporterDifferenceLabel(): string {
    if (!this.leftResult || !this.rightResult) return '';
    if (
      !this.leftResult.donorPaidExtraEstimated ||
      !this.rightResult.donorPaidExtraEstimated
    ) {
      return 'At least one optional supporter-paid amount is not estimated.';
    }
    const difference = Math.abs(
      this.leftResult.donorPaidExtraCents -
        this.rightResult.donorPaidExtraCents,
    );
    if (difference === 0) {
      return 'Supporters pay the same additional amount in both scenarios.';
    }
    return `${this.formatMoney(
      difference,
    )} difference in estimated supporter-paid additions.`;
  }

  get overviewUrl(): string {
    const selectedIds = new Set<PlatformId>([
      ...this.state.platformIds,
      this.leftPlatformId as PlatformId,
      this.rightPlatformId as PlatformId,
    ]);
    const overviewState = {
      ...this.state,
      platformIds: FUNDRAISING_PRICING_REGISTRY.canonicalPlatformOrder.filter(
        (id) => selectedIds.has(id),
      ),
    };
    return this.urlState.buildAbsoluteUrlForPath(
      overviewState,
      '/fundraising-platforms/',
    );
  }

  detailPageUrl(platformId: PlatformId): string | null {
    const path = getPricingConfiguration(platformId)?.detailPagePath;
    return path
      ? this.urlState.buildAbsoluteUrlForPath(this.state, path)
      : null;
  }

  organizationReceivesCents(result: PlatformComparisonResult): number {
    return Math.max(0, result.organizationRetainedCents);
  }

  costsExceedDonationsCents(result: PlatformComparisonResult): number {
    return Math.max(0, -result.organizationRetainedCents);
  }

  supporterPaidLabel(result: PlatformComparisonResult): string {
    return result.donorPaidExtraEstimated
      ? this.formatMoney(result.donorPaidExtraCents)
      : 'Optional, not estimated';
  }

  configurationSummary(result: PlatformComparisonResult): string {
    return result.optionGroups
      .map((group) => this.selectedSummary(result, group))
      .filter(Boolean)
      .join(' · ');
  }

  formatMoney(amountCents: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: amountCents % 100 === 0 ? 0 : 2,
    }).format(amountCents / 100);
  }

  private recalculate(updateUrl = true): void {
    const leftPlatformId = this.leftPlatformId as PlatformId;
    const rightPlatformId = this.rightPlatformId as PlatformId;
    const snapshot = this.calculationCoordinator.calculate(this.state, [
      leftPlatformId,
      rightPlatformId,
    ]);
    this.state = snapshot.state;
    this.scenario = snapshot.scenario;
    this.results = snapshot.results;
    this.copyStatus = '';

    if (this.leftResult && this.rightResult) {
      this.liveAnnouncement = `${this.leftResult.platformName} receives ${this.formatMoney(
        this.organizationReceivesCents(this.leftResult),
      )}. ${this.rightResult.platformName} receives ${this.formatMoney(
        this.organizationReceivesCents(this.rightResult),
      )}.`;
    }
    if (updateUrl) this.scheduleUrlUpdate();
    this.cdr.markForCheck();
  }

  private scheduleUrlUpdate(): void {
    if (this.urlUpdateTimeout) clearTimeout(this.urlUpdateTimeout);
    this.urlUpdateTimeout = setTimeout(() => {
      this.urlState.replace(this.state);
    }, 300);
  }

  private restoreFromUrl(): void {
    if (this.validatePlatformIds()) return;
    this.state = this.urlState.read();
    this.recalculate(false);
  }

  private trackScenarioChange(): void {
    if (!this.scenario) return;
    this.analytics.track('fundraising_versus_amount_changed', {
      amount_bucket: this.analytics.amountBucket(
        this.state.intendedGiftTotalCents,
      ),
      donation_count_bucket: this.analytics.donationCountBucket(
        this.scenario.transactions.length,
      ),
      profile_id: this.state.profileId,
      donation_count_source: this.scenario.donationCountSource ?? 'profile',
      left_platform: this.leftPlatformId,
      right_platform: this.rightPlatformId,
      page: 'versus',
    });
  }

  private selectedSummary(
    result: PlatformComparisonResult,
    group: PlatformOptionGroup,
  ): string {
    const choice = (group.choices ?? []).find(
      (candidate) => candidate.id === result.optionSelections[group.id],
    );
    const label = choice?.label ?? '';
    return group.summaryPrefix ? `${group.summaryPrefix}: ${label}` : label;
  }

  private validatePlatformIds(): string {
    const availableIds = new Set(
      getAvailablePricingConfigurations().map((platform) => platform.id),
    );
    if (!this.leftPlatformId || !this.rightPlatformId) {
      return 'This fundraising comparison is missing one or both platform IDs.';
    }
    if (
      !availableIds.has(this.leftPlatformId as PlatformId) ||
      !availableIds.has(this.rightPlatformId as PlatformId)
    ) {
      return 'One or both fundraising platforms are not available.';
    }
    if (this.leftPlatformId === this.rightPlatformId) {
      return 'Choose two different fundraising platforms to compare.';
    }
    return '';
  }
}
